/**
 * The «…» menu of screen 14b (D-68, salon-mockup.html servicedel): a bottom menu
 * on a phone, a dropdown right under «…» on desktop. It is the confirmation, so
 * the one action is red and nothing is done until it is chosen. Esc, «Отмена»
 * and a tap outside close it. Drawn over the whole app (the `.app` root), not
 * inside the sheet, so a scrolled sheet cannot move it.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { useDesktop } from '../lib/useDesktop';

export function ActionMenu({
  anchor,
  title,
  note,
  action,
  cancel,
  centered = false,
  onAction,
  onClose,
}: {
  /** The «…» button; the menu opens under it on desktop. */
  anchor: HTMLElement;
  /** A bold first line above the note (17c, D-89). */
  title?: string;
  note: string;
  /** 17c (D-89): mid-screen with a scrim and «Отмена» on desktop too, as
   *  there is no «…» to hang it from. */
  centered?: boolean;
  action: string;
  cancel: string;
  onAction: () => void;
  onClose: () => void;
}) {
  const desktop = useDesktop();
  const root = anchor.closest('.app');
  const first = useRef<HTMLButtonElement>(null);
  const [place, setPlace] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!desktop || !root || centered) return setPlace({});
    const a = root.getBoundingClientRect();
    const b = anchor.getBoundingClientRect();
    setPlace({ top: b.bottom - a.top + 6, right: a.right - b.right });
  }, [desktop, root, anchor, centered]);

  useEffect(() => {
    first.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!root) return null;
  return createPortal(
    <div className={`amenu-wrap${centered ? ' cfm-menu' : ''}`}>
      <div className="amenu-scrim" onClick={onClose} />
      <div className={`amenu${centered ? ' mid' : ''}`} role="menu" style={place}>
        <div className="grp">
          <div className="hd">
            {title && <b style={{ display: 'block', color: 'var(--ink)', marginBottom: 2 }}>{title}</b>}
            {note}
          </div>
          <button ref={first} type="button" className="it kill" role="menuitem" onClick={onAction}>
            {action}
          </button>
        </div>
        <div className="grp grp-cancel">
          <button type="button" className="it cancel" role="menuitem" onClick={onClose}>
            {cancel}
          </button>
        </div>
      </div>
    </div>,
    root,
  );
}
